import type { Currency } from '@packages/domain/models/currency';
import type { Price } from '@packages/domain/models/price';
import {
  FETCH_OFFERS_SUCCESS,
  FETCH_RECURRING_OFFERS_SUCCESS,
} from 'constants/offers';
import {
  CREATE_PAYMENT_SUCCESS,
  FETCH_PAYMENT_SUCCESS,
  FETCH_USER_PAYMENTS_SUCCESS,
} from 'constants/payment';
import {
  FETCH_RECIPIENT_FROM_PAYMENT_SUCCESS,
  FETCH_RECIPIENT_SUCCESS,
  FETCH_RECIPIENTS_SUCCESS,
} from 'constants/recipients';
import { currenciesCache } from 'hooks/useCurrencies/currenciesCache';
import type { Payment } from 'models/payment';
import type { Recipient } from 'models/recipient';
import type { RootState } from 'reducers/types';
import type { Middleware } from 'redux';

type Currencies = Record<string, Currency>;

type BridgeAction = {
  type: string;
  payload?: {
    payments?: Payment[];
    result?: string[];
    entities?: {
      currencies?: Currencies;
      recipients?: Record<string, Recipient>;
    };
  };
};

const fromEntities = (action: BridgeAction): Currencies =>
  action.payload?.entities?.currencies ?? {};

const fromPayments = (action: BridgeAction): Currencies => {
  const record: Currencies = {};

  (action.payload?.payments ?? []).forEach(payment => {
    const currency = (payment.price as Price | undefined)?.currency as
      | Currency
      | undefined;
    if (currency) {
      record[currency.code] = currency;
    }
  });

  return record;
};

const fromRecipients = (action: BridgeAction): Currencies => {
  const recipients = action.payload?.entities?.recipients ?? {};
  const record: Currencies = {};

  (action.payload?.result ?? []).forEach(id => {
    const currency = recipients[id]?.currency as Currency | undefined;
    if (currency) {
      record[currency.code] = currency;
    }
  });

  return record;
};

const extractCurrencies = (action: BridgeAction): Currencies => {
  switch (action.type) {
    case CREATE_PAYMENT_SUCCESS:
    case FETCH_OFFERS_SUCCESS:
    case FETCH_RECURRING_OFFERS_SUCCESS:
    case FETCH_RECIPIENT_SUCCESS:
    case FETCH_PAYMENT_SUCCESS:
    case FETCH_RECIPIENT_FROM_PAYMENT_SUCCESS:
      return fromEntities(action);
    case FETCH_USER_PAYMENTS_SUCCESS:
      return fromPayments(action);
    case FETCH_RECIPIENTS_SUCCESS:
      return fromRecipients(action);
    default:
      return {};
  }
};

const CurrenciesBridge: Middleware<unknown, RootState> =
  () => next => (action: unknown) => {
    currenciesCache.merge(extractCurrencies(action as BridgeAction));

    return next(action);
  };

export { CurrenciesBridge };
